import {
  Box,
  Popover,
  PopoverAnchor,
  PopoverArrow,
  PopoverContent,
  Spinner,
  Text,
  useDisclosure,
  useOutsideClick,
} from '@chakra-ui/react'
import { forwardRef, useCallback, useEffect, useRef, useState } from 'react'
import { useDebouncedCallback } from 'use-debounce'

import type { InputGroupProps } from './InputGroup'
import { InputGroup } from './InputGroup'

export type AutocompleteItem = {
  itemText?: string | React.ReactNode
  inputText: string
  value: string
  customData?: any
}

export type AutocompleteProps = {
  refetch: (value: string) => Promise<AutocompleteItem[]>
  onChange: (value: string) => void
  onSelect?: (item: AutocompleteItem) => void
  validFromLength?: number // minimalni delka platne hodnoty (napr ulice musi mit alespon 3 znaky nez se zacne vyhledavat)
  initLoad?: boolean
  value: string
} & Omit<InputGroupProps, 'onChange' | 'onSelect' | 'value'>

export const Autocomplete = forwardRef(function Autocomplete(
  {
    refetch,
    onChange,
    onSelect,
    validFromLength = 1,
    defaultValue,
    value,
    initLoad = false,
    ...props
  }: AutocompleteProps,
  ref,
) {
  const { isOpen, onOpen, onClose } = useDisclosure()
  const [isLoading, setIsLoading] = useState(false)
  const inputRef = useRef<HTMLInputElement | null>(null)
  const items = useRef<(HTMLDivElement | null)[]>([])
  const firstItemRef = useRef<HTMLDivElement | null>(null)
  const selectedItemRef = useRef<HTMLDivElement | null>(null)
  const contentRef = useRef<HTMLDivElement | null>(null)

  // kvuli logovani a debugovani odkud se otevira a zavira
  const closePopover = useCallback(() => onClose(), [onClose])
  const openPopover = useCallback(() => onOpen(), [onOpen])

  const isValidInput = useCallback(
    (val: string | undefined | null) => {
      return (val?.length ?? 0) >= validFromLength
    },
    [validFromLength],
  )

  // Váš vlastní hook pro načítání dat
  const [dataSource, setDataSource] = useState<AutocompleteItem[]>([])

  const loadData = (value: string, openList: boolean = true) => {
    if (isValidInput(value)) {
      refetch(value)
        .then((data) => setDataSource(data))
        .finally(() => {
          setIsLoading(false)
        })

      if (openList) openPopover()
    }
  }

  useEffect(() => {
    if (initLoad) loadData(value, false)
  }, [])

  // Debounced callback - 200ms zpozdeni nez se zacne nacitat (aby se pri rychlem psani nehledalo po kazdem pismenku)
  const handleChange = useDebouncedCallback((value) => {
    loadData(value)
  }, 200)

  // vyber z dropdownu
  const handleSelect = useCallback(
    (item: AutocompleteItem) => {
      onChange(item.inputText)
      onSelect?.(item)
      closePopover()
      setTimeout(() => inputRef.current?.focus())
    },
    [onChange, onSelect, closePopover],
  )

  // pri kliku mimo chceme naseptavac zavrit
  useOutsideClick({
    ref: inputRef as any,
    handler: (e) => {
      const clickedElementIsInsideContent = contentRef.current?.contains(e.target as Node)
      if (!clickedElementIsInsideContent)
        closePopover()
    },
  })

  useEffect(() => {
    // pokud se napr odmazava a uz neni nadale validni input, tak se zavre naseptavac
    if (!isValidInput(value)) closePopover()

    if (!isValidInput(value)) setIsLoading(false)
  }, [value, closePopover, setIsLoading, isValidInput])

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'ArrowDown') {
      openPopover()
      if (selectedItemRef.current) {
        selectedItemRef.current.focus()
        e.preventDefault()
      } else if (firstItemRef.current) {
        firstItemRef.current.focus()
        e.preventDefault()
      }
      e.stopPropagation()
    }
    if (e.key === 'Escape') {
      closePopover()
    }
  }

  const handleItemKeyDown = (e: React.KeyboardEvent, index: number, suggestion: AutocompleteItem) => {
    if (e.key === 'Enter' || e.key === ' ') handleSelect(suggestion)
    else if (e.key === 'Tab' || e.key === 'Escape') {
      inputRef.current?.focus()
      closePopover()
    }
    else if (e.key === 'ArrowDown') {
      if (index < dataSource.length - 1)
        items.current[index + 1]?.focus()
      else
        firstItemRef.current?.focus()
    }
    else if (e.key === 'ArrowUp') {
      if (index > 0) items.current[index - 1]?.focus()
      else
        items.current[dataSource.length - 1]?.focus()
    }
    else return

    e.preventDefault()
    e.stopPropagation()
  }

  return (
    <Popover isOpen={isOpen} matchWidth closeOnBlur closeOnEsc initialFocusRef={inputRef as any}>
      <PopoverAnchor>
        <InputGroup
          value={value}
          autoComplete="off"
          rightElement={isLoading && <Spinner />}
          onChange={(event) => {
            const val = event.target.value
            onChange(val)
            handleChange(val)
            if (isValidInput(val))
              setIsLoading(true)
          }}
          onFocus={(event) => {
            if (!isOpen && isValidInput(event.target.value)) {
              openPopover()
            }
          }}
          onClick={(event) => {
            if (!isOpen && isValidInput(value)) {
              openPopover()
            }
          }}
          onKeyDown={handleKeyDown}
          ref={inputRef}
          {...props}
        />
      </PopoverAnchor>

      {dataSource.length > 0 && (
        <PopoverContent
          sx={{
            display: 'grid',
            justifyContent: 'stretch',
            maxH: '500px',
            overflow: 'auto',
          }}
          ref={contentRef}
        >
          <PopoverArrow />
          {dataSource.map((suggestion, index) => {
            return (
              <Box
                key={index}
                sx={{
                  display: 'grid',
                  px: 2,
                  py: 1,
                  justifyContent: 'start',
                  cursor: 'pointer',
                  textAlign: 'start',
                  _hover: { backgroundColor: 'gray.100' },
                }}
                tabIndex={0}
                onClick={() => handleSelect(suggestion)}
                onKeyDown={(e) => handleItemKeyDown(e, index, suggestion)}
                ref={(node) => {
                  if (index === 0)
                    firstItemRef.current = node

                  if (suggestion.inputText == value)
                    selectedItemRef.current = node

                  items.current[index] = node
                }}
              >
                <Text fontSize="sm" fontWeight={suggestion.inputText == value ? 'bold' : ''}>
                  {suggestion.itemText ?? suggestion.inputText}
                </Text>
              </Box>
            )
          })}
        </PopoverContent>
      )}
    </Popover>
  )
})
